//deps
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

//components
import BottomInputHalfCardContent from "@cart-ui/components/bottom-input-half-card-content/BottomInputHalfCardContent";

//helpers and utils
import { isEmpty } from "@swiggy-private/js-utils";

//constants
import {
    CAKE_MESSAGE_INPUT_PLACEHOLDER,
    CAKE_SELECT_TEXT,
    EMOJI_REGEX,
    PRVEIW_IMAGE,
    LABEL,
    PREDEFINED_WORDS_SUFFIX,
    STATIC_ASSETS_PATH,
    STATIC_MESSAGE,
    ERROR_TEXT,
} from "../constant";
import { CAKE_TEST_IDS } from "@swiggy-private/cart-test-ids";

//types and interfaces
import { CartMessageOnCakeProps } from "@swiggy-private/cart-types";

const CartMessageOnCake = ({
    maxCharactersPerCakeMessage,
    predefinedWordsForCakeMessage,
    handleConfirmMessage,
    selectedCakeItem,
    onMessageHalfCardClick,
    onHalfCardClose,
}: CartMessageOnCakeProps) => {
    const [addCakeMessage, setAddCakeMessage] = useState(
        selectedCakeItem?.customer_request_messages?.[selectedCakeItem.index] || "",
    );
    const [isDisabled, setIsDisabled] = useState(true);
    const [editCakeMessage, setEditCakeMessage] = useState(false);
    const [errorText, setErrorText] = useState("");

    const previewImageProps = useMemo(
        () => ({
            cloudinaryId: !isEmpty(addCakeMessage) ? STATIC_ASSETS_PATH.FIRST_IMAGE : STATIC_ASSETS_PATH.SECOND_IMAGE,
            alt: PRVEIW_IMAGE.ALT,
            height: PRVEIW_IMAGE.HEIGHT,
            width: PRVEIW_IMAGE.WIDTH,
        }),
        [addCakeMessage],
    );

    useEffect(() => {
        if (!isEmpty(selectedCakeItem?.customer_request_messages?.[selectedCakeItem.index] || "")) {
            setEditCakeMessage(true);
            setIsDisabled(false);
        } else {
            setEditCakeMessage(false);
        }
    }, [selectedCakeItem]);

    const handleInputChange = (value: string) => {
        const sanitizedValue = value.replace(EMOJI_REGEX, "");
        setAddCakeMessage(sanitizedValue);

        if (!!errorText && sanitizedValue.trim().length <= maxCharactersPerCakeMessage) {
            setErrorText("");
        }

        if (sanitizedValue.trim().length > 0 || editCakeMessage) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    };

    const submitCakeMessage = useCallback(() => {
        if (!selectedCakeItem) return;
        setErrorText("");
        onMessageHalfCardClick();
        onHalfCardClose();
        const messageArray = selectedCakeItem.customer_request_messages || [];
        const index = selectedCakeItem.index;

        if (index < messageArray.length) {
            messageArray[index] = addCakeMessage;
        } else {
            while (messageArray.length < index) {
                messageArray.push("");
            }
            messageArray.push(addCakeMessage);
        }
        handleConfirmMessage(selectedCakeItem.unId, messageArray);
    }, [addCakeMessage, handleConfirmMessage, onHalfCardClose, onMessageHalfCardClick, selectedCakeItem]);

    const handleCharacterLimitExceeded = useCallback(() => setErrorText(ERROR_TEXT), []);

    const predefinedOptions = useMemo(
        () =>
            predefinedWordsForCakeMessage.map(word => ({
                label: `${word}${PREDEFINED_WORDS_SUFFIX}`,
                value: word,
            })),
        [predefinedWordsForCakeMessage],
    );

    return (
        <BottomInputHalfCardContent
            value={addCakeMessage}
            errorText={errorText}
            onChange={handleInputChange}
            onLimitExceed={handleCharacterLimitExceeded}
            placeholder={CAKE_MESSAGE_INPUT_PLACEHOLDER}
            maxLength={maxCharactersPerCakeMessage}
            ariaLabel={LABEL}
            inputTestId={CAKE_TEST_IDS.CAKE_MESSAGE_INPUT}
            previewContent={addCakeMessage || STATIC_MESSAGE}
            previewImage={previewImageProps}
            onPrimaryCtaClick={submitCakeMessage}
            primaryCtaText={editCakeMessage ? CAKE_SELECT_TEXT.SAVED : CAKE_SELECT_TEXT.CONFIRM_MESSAGE}
            isPrimaryCtaDisabled={isDisabled}
            primaryCtaTestId={CAKE_TEST_IDS.CAKE_MESSAGE_CONFIRM_BUTTON}
            predefinedOptions={predefinedOptions}
            containerTestId={CAKE_TEST_IDS.CAKE_MESSAGE_HALF_CARD}
        />
    );
};

export default memo(CartMessageOnCake);
